/**
 * Utility functions for league selection and date-based logic
 */

export interface League {
  id: string;
  name: string;
  description?: string;
  game?: string;
  format: string;
  participationPoints?: number;
  winPoints?: number;
  drawPoints?: number;
  lossPoints?: number;
  top8FirstPoints?: number;
  top8SecondPoints?: number;
  top8ThirdFourthPoints?: number;
  top8FifthEighthPoints?: number;
  commanderFirstPoints?: number;
  commanderSecondPoints?: number;
  commanderThirdPoints?: number;
  commanderFourthPoints?: number;
  premiumMultiplier?: number;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  maxParticipants?: number;
}

/**
 * Determines which league should be considered "current" based on date ranges
 * Priority order:
 * 1. Active leagues currently running (today is between startDate and endDate)
 * 2. Active leagues that haven't started yet (startDate is in the future)
 * 3. Active leagues that just ended (endDate is recent, within last 30 days)
 * 4. First active league by start date
 * 5. First league by start date (if no active leagues)
 */
export function getCurrentLeague(leagues: League[]): League | null {
  if (!leagues || leagues.length === 0) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

  // Filter to active leagues only
  const activeLeagues = leagues.filter((league) => league.isActive);

  // If no active leagues, fall back to all leagues
  const leaguesToConsider = activeLeagues.length > 0 ? activeLeagues : leagues;

  // 1. Currently running leagues (today is between startDate and endDate)
  const currentlyRunning = leaguesToConsider.filter((league) => {
    const startDate = new Date(league.startDate);
    startDate.setHours(0, 0, 0, 0);

    if (league.endDate) {
      const endDate = new Date(league.endDate);
      endDate.setHours(23, 59, 59, 999); // End of day
      return today >= startDate && today <= endDate;
    } else {
      // If no end date, consider it running if it has started
      return today >= startDate;
    }
  });

  if (currentlyRunning.length > 0) {
    // Return the most recently started among currently running leagues
    return currentlyRunning.sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    )[0];
  }

  // 2. Upcoming leagues (startDate is in the future)
  const upcoming = leaguesToConsider.filter((league) => {
    const startDate = new Date(league.startDate);
    startDate.setHours(0, 0, 0, 0);
    return today < startDate;
  });

  if (upcoming.length > 0) {
    // Return the soonest upcoming league
    return upcoming.sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    )[0];
  }

  // 3. Recently ended leagues (endDate is within last 30 days)
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const recentlyEnded = leaguesToConsider.filter((league) => {
    if (!league.endDate) return false;
    const endDate = new Date(league.endDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate >= thirtyDaysAgo && endDate < today;
  });

  if (recentlyEnded.length > 0) {
    // Return the most recently ended league
    return recentlyEnded.sort(
      (a, b) => new Date(b.endDate!).getTime() - new Date(a.endDate!).getTime()
    )[0];
  }

  // 4. Fallback: First active league by start date
  if (activeLeagues.length > 0) {
    return activeLeagues.sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    )[0];
  }

  // 5. Last resort: First league by start date
  return leaguesToConsider.sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  )[0];
}

/**
 * Gets the league status based on current date
 */
export function getLeagueStatus(league: League): {
  status: "running" | "upcoming" | "ended" | "inactive";
  message: string;
} {
  if (!league.isActive) {
    return { status: "inactive", message: "Inactive" };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = new Date(league.startDate);
  startDate.setHours(0, 0, 0, 0);

  if (league.endDate) {
    const endDate = new Date(league.endDate);
    endDate.setHours(23, 59, 59, 999);

    if (today >= startDate && today <= endDate) {
      return { status: "running", message: "Currently Running" };
    } else if (today < startDate) {
      return { status: "upcoming", message: "Upcoming" };
    } else {
      return { status: "ended", message: "Ended" };
    }
  } else {
    if (today >= startDate) {
      return { status: "running", message: "Currently Running" };
    } else {
      return { status: "upcoming", message: "Upcoming" };
    }
  }
}

/**
 * Sorts leagues by relevance (current league first, then by date)
 */
export function sortLeaguesByRelevance(
  leagues: League[],
  currentLeagueId?: string
): League[] {
  return [...leagues].sort((a, b) => {
    // Current league first
    if (currentLeagueId) {
      if (a.id === currentLeagueId) return -1;
      if (b.id === currentLeagueId) return 1;
    }

    // Then by status priority
    const aStatus = getLeagueStatus(a);
    const bStatus = getLeagueStatus(b);

    const statusPriority = { running: 0, upcoming: 1, ended: 2, inactive: 3 };
    const aPriority = statusPriority[aStatus.status];
    const bPriority = statusPriority[bStatus.status];

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // Finally by start date (most recent first)
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  });
}
